import { Block, Fragment, type Inline, type NodeType, type Text, type Value } from '@gitbook/slate';

import { createParagraph } from '../paragraph';
import { BLOCK_EXPANDABLE, FRAGMENT_EXPANDABLE_BODY, FRAGMENT_EXPANDABLE_TITLE } from '../types';

/**
 * Create an expandable block.
 */
export function createExpandableBlock(
    title: string | Block | Array<Inline | Text> = '',
    body: Array<Block> = [createParagraph()]
): Block {
    return Block.create({
        type: BLOCK_EXPANDABLE,
        isVoid: true,
        fragments: [
            Fragment.create({
                fragment: FRAGMENT_EXPANDABLE_TITLE,
                type: FRAGMENT_EXPANDABLE_TITLE,
                nodes: [Block.isBlock(title) ? title : createParagraph(title)],
            }),
            Fragment.create({
                fragment: FRAGMENT_EXPANDABLE_BODY,
                type: FRAGMENT_EXPANDABLE_BODY,
                nodes: body,
            }),
        ],
    });
}

/**
 * Get the title fragment of an expandable block.
 */
export function getTitleFragment(block: Block): Fragment {
    const fragment = block.getFragmentByKey(FRAGMENT_EXPANDABLE_TITLE);
    if (!fragment) {
        throw new Error('Expandable block is missing title fragment');
    }
    return fragment;
}

/**
 * Get the body fragment of an expandable block.
 */
export function getBodyFragment(block: Block): Fragment {
    const fragment = block.getFragmentByKey(FRAGMENT_EXPANDABLE_BODY);
    if (!fragment) {
        throw new Error('Expandable block is missing body fragment');
    }
    return fragment;
}

/**
 * Return the currently selected expandable block.
 */
export function getCurrentExpandable(value: Value):
    | {
          expandable: Block;
          fragment: Fragment | null;
      }
    | undefined {
    if (!value.startKey) {
        return undefined;
    }

    if (!value.document.hasNode(value.startKey)) {
        return undefined;
    }

    const expandable = value.document.getClosest(
        value.startKey,
        (node: NodeType) => node.object === 'block' && node.type === BLOCK_EXPANDABLE
    );

    if (!expandable) {
        return undefined;
    }

    // @ts-expect-error
    return { expandable, fragment: value.selection.hasFocusInFragmentOf(expandable) };
}
